const COLORS = ['blue', 'yellow'];
const PACKAGE_TYPES = ['box', 'envelope'];

function activePair(pair, fallback) {
  return Array.isArray(pair) && pair.length === 2 && pair[0] !== pair[1]
    ? pair
    : fallback;
}

function hashSeed(value) {
  let hash = 2166136261;
  for (const char of String(value || 'cargo')) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6D2B79F5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function cargoSideFor(rule, item) {
  if (rule.groups) {
    const value = rule.rule_type === 'color' ? item.color : item.package_type;
    if (rule.groups.left.includes(value)) return 'left';
    if (rule.groups.right.includes(value)) return 'right';
    throw new Error(`Cargo V4 has no destination for ${value}`);
  }
  const firstValue = activePair(
    rule.values,
    rule.rule_type === 'color' ? COLORS : PACKAGE_TYPES,
  )[0];
  const featureValue = rule.rule_type === 'color' ? item.color : item.package_type;
  const normalSide = featureValue === firstValue ? 'left' : 'right';
  if (rule.mapping === 'reverse') return normalSide === 'left' ? 'right' : 'left';
  return normalSide;
}

function isIncompatible(item, rule, configVersion, colors, packageTypes) {
  if (Number(configVersion) >= 2) {
    const otherRule = {
      rule_type: rule.rule_type === 'color' ? 'package_type' : 'color',
      mapping: 'normal',
      values: rule.rule_type === 'color' ? packageTypes : colors,
    };
    return cargoSideFor(rule, item) !== cargoSideFor(otherRule, item);
  }
  const colorSide = item.color === 'blue' ? 'left' : 'right';
  const typeSide = item.package_type === 'box' ? 'left' : 'right';
  return colorSide !== typeSide;
}

function comboKey(item) {
  return `${item.color}-${item.package_type}`;
}

function allowedBySequence(candidate, sequence, rule, previousItem) {
  const last = sequence[sequence.length - 1] || previousItem;
  if (!last) return true;
  if (comboKey(last) === comboKey(candidate)) {
    const beforeLast = sequence[sequence.length - 2];
    if (beforeLast && comboKey(beforeLast) === comboKey(candidate)) return false;
    if (!sequence.length) return false;
  }

  const side = cargoSideFor(rule, candidate);
  let sameSideRun = 0;
  for (let index = sequence.length - 1; index >= 0; index -= 1) {
    if (cargoSideFor(rule, sequence[index]) !== side) break;
    sameSideRun += 1;
  }
  if (sameSideRun >= 3) return false;

  let alternatingRun = 1;
  let expectedSide = side;
  for (let index = sequence.length - 1; index >= 0; index -= 1) {
    const priorSide = cargoSideFor(rule, sequence[index]);
    if (priorSide === expectedSide) break;
    alternatingRun += 1;
    expectedSide = priorSide;
  }
  return alternatingRun <= 4;
}

function balancedPool(count, incompatiblePct, fixedColor, fixedPackageType, rule, configVersion, colors, packageTypes) {
  if (fixedPackageType) {
    return Array.from({ length: count }, (_, index) => ({
      color: colors[index % colors.length],
      package_type: fixedPackageType,
    }));
  }
  if (fixedColor) {
    return Array.from({ length: count }, (_, index) => ({
      color: fixedColor,
      package_type: packageTypes[index % packageTypes.length],
    }));
  }

  const combinations = colors.flatMap((color) => packageTypes.map((packageType) => ({
    color,
    package_type: packageType,
  })));
  const incompatible = combinations.filter((item) => isIncompatible(item, rule, configVersion, colors, packageTypes));
  const compatible = combinations.filter((item) => !isIncompatible(item, rule, configVersion, colors, packageTypes));
  const incompatibleCount = Math.round(count * Number(incompatiblePct || 0) / 100);
  const compatibleCount = count - incompatibleCount;
  return [
    ...Array.from({ length: incompatibleCount }, (_, index) => ({ ...incompatible[index % incompatible.length] })),
    ...Array.from({ length: compatibleCount }, (_, index) => ({ ...compatible[index % compatible.length] })),
  ];
}

// V4 has 3-5 colors (or 2-4 package types) and precisely two destinations.
// Generate a balanced destination pool while cycling through every legal
// color/type combination; do not silently drop formerly unlocked content.
export function validateCargoGroupRule(rule, colors, types) {
  if (!rule || !['color', 'package_type'].includes(rule.rule_type)) throw new Error('Invalid V4 feature');
  const values = rule.rule_type === 'color' ? colors : types;
  if (!Array.isArray(values) || values.length < 2 || !rule.groups ||
      !Array.isArray(rule.groups.left) || !Array.isArray(rule.groups.right) ||
      !rule.groups.left.length || !rule.groups.right.length) throw new Error('Incomplete V4 cargo groups');
  const grouped = [...rule.groups.left, ...rule.groups.right];
  if (new Set(grouped).size !== grouped.length || grouped.length !== values.length ||
      !values.every(value => grouped.includes(value))) throw new Error('V4 groups must partition every active value');
}

function groupedPool(count, rule, colors, types) {
  validateCargoGroupRule(rule, colors, types);
  const all = colors.flatMap(color => types.map(packageType => ({
    color, package_type: packageType,
  })));
  const left = all.filter(item => cargoSideFor(rule, item) === 'left');
  const right = all.filter(item => cargoSideFor(rule, item) === 'right');
  const leftCount = Math.ceil(count / 2);
  const rightCount = count - leftCount;
  return [
    ...Array.from({ length: leftCount }, (_, i) => ({ ...left[i % left.length] })),
    ...Array.from({ length: rightCount }, (_, i) => ({ ...right[i % right.length] })),
  ];
}

export function generateCargoSequence({
  seed,
  rule,
  count = 64,
  incompatiblePct = 50,
  fixedColor,
  fixedPackageType,
  previousItem,
  configVersion = 1,
  colorPair,
  packageTypePair,
  colorValues,
  packageTypeValues,
  focusValue,
}) {
  const grouped = Number(configVersion) >= 4 && !!rule?.groups;
  const colors = grouped ? colorValues : activePair(colorPair, COLORS);
  const packageTypes = grouped ? packageTypeValues : activePair(packageTypePair, PACKAGE_TYPES);
  if (grouped && focusValue) {
    const active = rule.rule_type === 'color' ? colors : packageTypes;
    if (!active.includes(focusValue)) throw new Error('Focused V4 item must be active');
  }
  const random = mulberry32(hashSeed(`${seed}:${rule.rule_type}:${rule.mapping}:${rule.starts_at_seconds || 0}`));
  const remaining = grouped
    ? groupedPool(count, rule, colors, packageTypes)
    : balancedPool(count, incompatiblePct, fixedColor, fixedPackageType, rule, configVersion, colors, packageTypes);
  const sequence = [];
  while (remaining.length) {
    const allowed = remaining
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => allowedBySequence(item, sequence, rule, previousItem));
    // An unlocked color/type must not be absent from the player's short
    // 40-second round merely because the full 80-item pool is shuffled.
    // Reorder only: the balanced left/right inventory is not changed.
    const field = rule.rule_type === 'color' ? 'color' : 'package_type';
    const alreadyFocused = focusValue
      ? sequence.filter(item => item[field] === focusValue).length
      : 0;
    const focused = grouped && focusValue && sequence.length < 6 && alreadyFocused < 2
      ? allowed.filter(({ item }) => item[field] === focusValue)
      : [];
    const choices = focused.length
      ? focused
      : allowed.length ? allowed : remaining.map((item, index) => ({ item, index }));
    const selected = choices[Math.floor(random() * choices.length)];
    sequence.push({ ...selected.item, id: `${seed}-${rule.starts_at_seconds || 0}-${sequence.length}` });
    remaining.splice(selected.index, 1);
  }
  return sequence;
}
